/**
 * Deep-link handoff: the `?token=…&redirectTo=…` links we send by email and
 * WhatsApp log the user in and then land them somewhere specific.
 *
 * The journey spans a full page load:
 *   1. `/?token=…` boots, `useLoginTokenToRenew` exchanges the token.
 *   2. On success it does `window.location.href = redirectTo` (a real reload,
 *      so sockets and caches start clean under the new identity).
 *   3. The target route boots and settles (e.g. the chat page selects the
 *      linked conversation).
 *
 * Without a loader the user watches the homepage, a reload, the chat list and
 * finally the conversation flash by. `DeepLinkLoader` covers all three steps;
 * these helpers carry its state across the reload in sessionStorage.
 */

export const HANDOFF_STORAGE_KEY = "nbyula.deepLinkHandoff";

// A marker older than this is stale (a crashed or abandoned handoff), never a
// reason to cover a page the user opened later.
export const HANDOFF_TTL_MS = 30 * 1000;

// Hard cap on how long the loader may cover the destination page, whatever
// the route does. The page underneath stays usable once it lifts.
export const HANDOFF_MAX_COVER_MS = 10 * 1000;

const CONVERSATION_PATH = /^\/conversations\/([^/?#]+)\/?$/;

export function hasLoginToken(search = "") {
  return Boolean(new URLSearchParams(search).get("token"));
}

/**
 * Turns the raw `redirectTo` query value into a same-origin path.
 *
 * `URLSearchParams.get` already decodes once; some senders double-encode, so
 * a second decode is attempted but a malformed `%` sequence must not throw.
 * Anything pointing off-site (`https://evil.com`, `//evil.com`,
 * `javascript:`) falls back to "/" — a token link must never be an open
 * redirect.
 */
export function sanitizeRedirectPath(raw, origin = window.location.origin) {
  if (!raw || raw === "null" || raw === "undefined") return "/";

  let value = raw;
  try {
    value = decodeURIComponent(raw);
  } catch (error) {
    // Keep the single-decoded value.
  }

  try {
    const url = new URL(value, origin);
    if (url.origin !== origin) return "/";
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    return "/";
  }
}

function pathnameOf(path) {
  try {
    return new URL(path, "http://handoff.local").pathname;
  } catch (error) {
    return "/";
  }
}

function getStorage() {
  try {
    return window.sessionStorage;
  } catch (error) {
    // Blocked storage (private mode, sandboxed iframe): no cross-reload loader.
    return null;
  }
}

/** Records that a login just succeeded and the browser is reloading into `target`. */
export function markHandoffPending(target, now = Date.now()) {
  try {
    getStorage()?.setItem(
      HANDOFF_STORAGE_KEY,
      JSON.stringify({ target, pathname: pathnameOf(target), at: now })
    );
  } catch (error) {
    // Quota / disabled storage: the reload simply lands without the loader.
  }
}

export function clearHandoff() {
  try {
    getStorage()?.removeItem(HANDOFF_STORAGE_KEY);
  } catch (error) {
    // Nothing to clear.
  }
}

/**
 * The pending handoff for the page being booted, or null.
 *
 * Only honoured on the page it was written for and within the TTL; anything
 * else is cleared so a stale marker can never cover an unrelated page.
 */
export function readPendingHandoff(pathname, now = Date.now()) {
  let handoff = null;
  try {
    handoff = JSON.parse(getStorage()?.getItem(HANDOFF_STORAGE_KEY) || "null");
  } catch (error) {
    handoff = null;
  }
  if (!handoff) return null;

  const isFresh =
    typeof handoff.at === "number" && now - handoff.at <= HANDOFF_TTL_MS;
  if (!isFresh || handoff.pathname !== pathname) {
    clearHandoff();
    return null;
  }
  return handoff;
}

/** The conversation a `/conversations/:id` path opens, or null for any other route. */
export function getConversationIdFromPath(pathname = "") {
  return pathname.match(CONVERSATION_PATH)?.[1] || null;
}

/**
 * Is the destination ready to be uncovered?
 *   true  — yes (after a short grace for its first paint)
 *   false — no, it is still loading something we can observe
 *   null  — no observable signal: uncover once the route has been stable for
 *           a moment
 *
 * Only parent→child routes whose loading is already published in Redux get a
 * rule; everything else is null. A route that redirects after mount
 * (`/quest/:id` → `/q/:slug`, `/comments/:id` → its quest or listing,
 * planner/settlements bounces) also lands on null, and because the settle
 * timer restarts on every navigation the loader stays up through the chain.
 *
 * @param handoff          the pending handoff ({ target, pathname })
 * @param chat             `state.chat`
 * @param currentPathname  the router's pathname now
 * @param sawUrlLoading    the chat page has flagged this conversation as loading
 */
export function getDestinationReadiness(
  handoff,
  chat = {},
  currentPathname,
  sawUrlLoading = false
) {
  if (!handoff) return true;
  if (currentPathname !== handoff.pathname) return null;

  const params = new URL(handoff.target, "http://handoff.local").searchParams;
  const conversations = chat.conversations || [];

  // /conversations/:id[?topicId] — list → conversation → topic.
  const conversationId = getConversationIdFromPath(handoff.pathname);
  if (conversationId) {
    if (chat.selectedConversationId !== conversationId) {
      // The chat page clears its URL-loading flag on every outcome; if it did
      // so without selecting ours (unknown room, group-join modal), stop.
      return sawUrlLoading && !chat.urlConversationLoading?.isLoading;
    }
    const topicId = params.get("topicId");
    if (!topicId || chat.selectedConversationTopic?._id === topicId) return true;
    // The linked topic is not among the room's loaded topics: stop waiting.
    const topics = chat.topics?.[conversationId];
    return (
      Array.isArray(topics) &&
      !chat.isConversationTopicLoading &&
      !topics.some((topic) => topic?._id === topicId)
    );
  }

  // /conversations[?filter=unread&select=dm|group] — list → auto-selected room.
  if (handoff.pathname === "/conversations") {
    if (!conversations.length) return false;
    if (!params.get("select") || chat.selectedConversationId) return true;
    return null; // list is in; give the auto-select a moment
  }

  return null;
}

/**
 * Perceived progress. Each stage eases from its floor toward (never reaching)
 * its cap, so the bar keeps moving however long the network takes and only
 * fills when the destination is actually ready. Elapsed time is
 * `performance.now()` — time since this page's navigation started — so the
 * static splash in index.html (which mirrors these numbers; keep in sync)
 * and the React overlay draw the same bar, and the reload hands off from
 * ~45% to 50% instead of restarting.
 */
export const HANDOFF_STAGES = {
  signingIn: { floor: 0.08, cap: 0.45, tauMs: 1200 },
  opening: { floor: 0.5, cap: 0.94, tauMs: 1800 },
};

// After this long on a page, a reassurance line appears under the caption.
export const HANDOFF_REASSURE_AFTER_MS = 3000;

// After this long on a page, offer a reload — the same escape hatch the boot
// splash offers. Only reachable while signing in (the destination stage is
// capped at HANDOFF_MAX_COVER_MS), e.g. when the login request hangs.
export const HANDOFF_RELOAD_AFTER_MS = 15 * 1000;

export function getHandoffProgress(stage, elapsedMs) {
  const { floor, cap, tauMs } = HANDOFF_STAGES[stage] || HANDOFF_STAGES.opening;
  const eased = 1 - Math.exp(-Math.max(0, elapsedMs) / tauMs);
  return floor + (cap - floor) * eased;
}

/** What the loader tells the user it is doing, by destination. */
export function getHandoffCaption(pathname) {
  if (getConversationIdFromPath(pathname)) return "Opening your conversation…";
  return "Taking you there…";
}
